// =============================================================================
// ui/tabs/CustomWidgetCard.tsx — one card on the Build-your-own dashboard.
// -----------------------------------------------------------------------------
// A metric picker per series, a shape toggle per series, the period grouping,
// cumulative, the long/short split, and the chart. Fully controlled — it owns
// no state at all, so the same card renders identically whether its widgets
// live in React state or in the trader's saved preferences.
//
// ── 🚨 THE COLOUR DOT IS A CONTROL, NOT A BULLET ──
//
// It looks like a legend dot in a screenshot and it is a native
// `<input type="color">` — click it and the OS colour picker opens. Reading the
// screenshot alone, this would have shipped as decoration and nobody would have
// filed it, because a dot that does nothing looks exactly like a dot.
//
// ⚠ EXCEPT IN LONG/SHORT MODE, where the two colours are fixed. A long/short
// pair has to read the same on every widget, so there the dot really is a
// bullet — and it must not be a disabled input, it must not be an input.
//
// ── ⚠ `cum` IS A BADGE. `Σ` IS THE BUTTON ──
//
// Two things about the same fact, side by side, and only one of them is
// clickable. The badge appears only while cumulative is on, which is why the
// screenshot shows it on the first widget and none of the others.
//
// ── ⚠ WHICH CONTROLS EXIST DEPENDS ON THE METRIC, NOT THE WIDGET ──
//
// `L/S` is offered only for a SINGLE metric that supports the split — three of
// the fifteen do. `+ overlay` is hidden entirely while the split is on, and once
// four metrics are stacked. The old client hides these rather than disabling
// them, so a missing toggle is the design, not a rendering failure.
// =============================================================================

import {
  CUSTOM_CATEGORIES, CW_MAX_OVERLAY, CW_PALETTE, LONG_COLOR, SHORT_COLOR,
  buildCustomSeries, metricsInCategory, splitApplies, widgetSeries,
  type ChartType, type CustomWidget, type GroupBy, type MetricTrade,
} from '../../selectors/customAnalytics';
// The edits, aliased so the JSX below reads as what it does rather than as a
// wall of near-identical names.
import {
  addOverlay as overlay, changeGroupBy as setGroup, changeMetric as pickMetric,
  changeSeriesChart as setChart, recolorSeries as recolor,
  removeMetric as dropMetric, toggleCumulative as toggleCum,
  toggleDirectional as toggleDir,
} from '../../selectors/customAnalytics';
import type { DayBoundaryMode } from '../../selectors/range';
import { CustomChart } from '../charts/CustomChart';
import { metricValue } from '../format';

/** The old client's shape glyphs. */
const CHART_GLYPH: Record<ChartType, string> = { line: '∿', area: '◢', bar: '▮' };
const CHART_ORDER: readonly ChartType[] = ['line', 'area', 'bar'];
const GROUP_LABEL: Record<GroupBy, string> = { day: 'D', week: 'W', month: 'M' };

export interface CustomWidgetCardProps {
  widget: CustomWidget;
  trades: readonly MetricTrade[];
  mode: DayBoundaryMode;
  onChange: (next: CustomWidget) => void;
  onRemove: () => void;
}

export function CustomWidgetCard(
  { widget: w, trades, mode, onChange, onRemove }: CustomWidgetCardProps,
) {
  const metrics = widgetSeries(w);
  const split = splitApplies(metrics, w.directional);
  const { points, series } = buildCustomSeries({
    trades,
    metrics,
    groupBy: w.groupBy,
    cumulative: w.cumulative,
    directional: w.directional,
    chartTypes: w.chartTypes,
    colors: w.colors,
    mode,
  });

  const dotColor = (i: number): string => (
    w.colors?.[i] || (CW_PALETTE[i % CW_PALETTE.length] as string)
  );

  const latest = points.length ? points[points.length - 1] : null;
  const heading = split
    ? `${metrics[0]?.label ?? ''}, long versus short`
    : metrics.map((m) => m.label).join(' and ');

  return (
    <div className="cw-card">
      <div className="cw-head">
        <span className="cw-metrics">
          {metrics.map((m, i) => (
            <span className="cw-metric-row" key={`${m.id}-${i}`}>
              {/* 🚨 A control, except in split mode. See the header. */}
              {split ? (
                <i className="cw-dot" style={{ background: LONG_COLOR }} />
              ) : (
                <label className="cw-dot cw-dot-input" style={{ background: dotColor(i) }}>
                  <input
                    type="color"
                    aria-label={`Colour for ${m.label}`}
                    value={dotColor(i)}
                    onChange={(e) => onChange(recolor(w, i, e.target.value))}
                  />
                </label>
              )}

              <select
                className="cw-metric"
                aria-label={`Metric ${i + 1}`}
                value={m.id}
                onChange={(e) => onChange(pickMetric(w, i, e.target.value))}
              >
                {CUSTOM_CATEGORIES.map((cat) => (
                  <optgroup label={cat} key={cat}>
                    {metricsInCategory(cat).map((opt) => (
                      <option value={opt.id} key={opt.id}>{opt.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>

              {/* Per-series shape, so an overlay can mix bars under a line. */}
              <span className="cw-typeseg">
                {CHART_ORDER.map((tp) => (
                  <button
                    type="button"
                    key={tp}
                    className={`cw-tbtn${w.chartTypes[i] === tp ? ' on' : ''}`}
                    title={tp}
                    aria-label={`${m.label} as ${tp}`}
                    aria-pressed={w.chartTypes[i] === tp}
                    onClick={() => onChange(setChart(w, i, tp))}
                  >
                    {CHART_GLYPH[tp]}
                  </button>
                ))}
              </span>

              {metrics.length > 1 && (
                <button
                  type="button"
                  className="cw-rmm"
                  title="Remove this metric"
                  aria-label={`Remove ${m.label}`}
                  onClick={() => onChange(dropMetric(w, i))}
                >
                  ×
                </button>
              )}
            </span>
          ))}

          {/* ⚠ Hidden — not disabled — while split, or once four are stacked. */}
          {!split && metrics.length < CW_MAX_OVERLAY && (
            <button
              type="button"
              className="cw-overlay"
              title="Overlay another metric"
              onClick={() => onChange(overlay(w))}
            >
              + overlay
            </button>
          )}
        </span>

        <span className="cw-ctrls">
          {w.cumulative && <span className="cw-cum">cum</span>}

          <span className="cw-seg">
            {(['day', 'week', 'month'] as GroupBy[]).map((g) => (
              <button
                type="button"
                key={g}
                className={`cw-seg-btn${w.groupBy === g ? ' on' : ''}`}
                aria-label={`Group by ${g}`}
                aria-pressed={w.groupBy === g}
                onClick={() => onChange(setGroup(w, g))}
              >
                {GROUP_LABEL[g]}
              </button>
            ))}
          </span>

          <button
            type="button"
            className={`cw-seg-btn solo${w.cumulative ? ' on' : ''}`}
            title="Cumulative"
            aria-label="Cumulative"
            aria-pressed={w.cumulative}
            onClick={() => onChange(toggleCum(w))}
          >
            Σ
          </button>

          {/* ⚠ Offered by the METRIC, not the widget — three of fifteen. */}
          {metrics.length === 1 && metrics[0]?.directional && (
            <button
              type="button"
              className={`cw-seg-btn solo${w.directional ? ' on' : ''}`}
              title="Split Long vs Short"
              aria-label="Split long versus short"
              aria-pressed={w.directional}
              onClick={() => onChange(toggleDir(w))}
            >
              L/S
            </button>
          )}

          <button
            type="button"
            className="cw-rm"
            title="Remove widget"
            aria-label="Remove widget"
            onClick={onRemove}
          >
            ×
          </button>
        </span>
      </div>

      <CustomChart points={points} series={series} label={heading} />

      {latest && (
        <div className="cw-legend">
          {split ? (
            <>
              <span>
                <i style={{ background: LONG_COLOR }} />
                Long {metricValue(latest.values['long'], metrics[0]?.unit ?? '')}
              </span>
              <span>
                <i style={{ background: SHORT_COLOR }} />
                Short {metricValue(latest.values['short'], metrics[0]?.unit ?? '')}
              </span>
            </>
          ) : (
            metrics.map((m, i) => (
              <span key={`${m.id}-${i}`}>
                <i style={{ background: dotColor(i) }} />
                {m.label} {metricValue(latest.values[m.id], m.unit)}
              </span>
            ))
          )}
        </div>
      )}
    </div>
  );
}
